import { useEffect, useState } from 'react'
import { Bell, Loader2 } from 'lucide-react'
import { showToast } from './Toast'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'
import { needsA2hsForPush, pushSupported, subscribeToPush } from '../lib/webPush'

/**
 * #1384 Phase 2 お知らせ push の受信設定。
 *
 * 業務通知（#1230 の注文呼び出し）とは別に切れる。#2004 で**既定はオン**にした
 * （同意は利用規約で取る。通知を許可した人には届く）。ここで切った人にだけ届かない。
 *
 * 状態は**サーバを正とする**。localStorage で覚えると端末間でズレて
 * 「オンにしたのにオフに見える」ことが起きる。
 */
const TOPIC = 'announcements'

export default function AnnouncementOptIn() {
  const { t, locale } = useI18n()
  const [enabled, setEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    api.getPushTopics()
      .then((res) => { if (!cancelled) setEnabled(res.topics.includes(TOPIC)) })
      .catch(() => { /* 未ログイン・未購読なら false のまま */ })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // 通知が使えない環境ではトグル自体を出さない。押しても何も起きない
  // スイッチを見せる方が不親切なため（iOS Safari はホーム画面追加が前提）。
  if (loading || !pushSupported() || needsA2hsForPush()) return null

  const toggle = async (next: boolean) => {
    if (busy) return
    setBusy(true)
    const previous = enabled
    setEnabled(next)                       // 楽観更新（失敗時に戻す）
    try {
      if (next) {
        // 通知の許可と push 購読を先に確立する。購読が無いと topics を
        // 書き込む先が無く、オンにしたつもりで届かない状態になる。
        const result = await subscribeToPush(locale)
        if (result !== 'ok') {
          // 黙って戻すと「押しても何も起きないスイッチ」になる。理由を出す。
          showToast({
            type: 'error',
            message: t(result === 'denied' ? 'announcement.optInDenied' : 'announcement.optInUnavailable'),
          })
          setEnabled(previous)
          return
        }
      }
      await api.setPushTopics(next ? [TOPIC] : [])
    } catch (e) {
      console.error('[AnnouncementOptIn] toggle failed:', e)
      setEnabled(previous)
    } finally {
      setBusy(false)
    }
  }

  return (
    <label className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-card">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-matcha/15">
        <Bell size={17} className="text-matcha" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-body text-sm font-semibold text-text-primary">
          {t('announcement.optInTitle')}
        </p>
        <p className="mt-0.5 font-body text-xs leading-snug text-text-tertiary">
          {t('announcement.optInDesc')}
        </p>
      </div>
      {busy ? (
        <Loader2 size={18} className="mt-1 shrink-0 animate-spin text-text-tertiary" />
      ) : (
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => void toggle(e.target.checked)}
          className="mt-1 h-5 w-5 shrink-0 accent-yokoso-red"
          aria-label={t('announcement.optInTitle')}
        />
      )}
    </label>
  )
}
